import { useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * Custom hook to handle driver on-way notifications
 * Sends notifications for the next two deliveries in sequence when driver is "encamino"
 */
export function useDriverOnWayNotification(deliveries, setDeliveries) {
  const notifyKickoffRef = useRef(false);

  useEffect(() => {
    if (!deliveries?.length || notifyKickoffRef.current) return;

    // Order strictly by sequence (ignore items without a numeric sequence)
    const bySeq = deliveries
      .filter(d => Number.isFinite(d.sequence))
      .slice()
      .sort((a, b) => a.sequence - b.sequence);

    if (!bySeq.length) return;

    // Find the LAST index whose status is one of the "closed" statuses
    const CLOSED = new Set(['entregado', 'cancelado', 'rechazado']);
    let lastClosedIdx = -1;
    
    for (let i = bySeq.length - 1; i >= 0; i--) {
      const s = (bySeq[i]?.status || '').toLowerCase();
      if (CLOSED.has(s)) {
        lastClosedIdx = i;
        break;
      }
    }

    // Take the NEXT TWO items by position (no skipping ahead)
    const nextTwo = [];
    if (lastClosedIdx === -1) {
      // No closed yet → take the first two in sequence order
      if (bySeq[0]) nextTwo.push(bySeq[0]);
      if (bySeq[1]) nextTwo.push(bySeq[1]);
    } else {
      if (bySeq[lastClosedIdx + 1]) nextTwo.push(bySeq[lastClosedIdx + 1]);
      if (bySeq[lastClosedIdx + 2]) nextTwo.push(bySeq[lastClosedIdx + 2]);
    }

    if (!nextTwo.length) return;

    // From those two, send only if (encamino && not sent yet)
    const candidates = nextTwo.filter(d =>
      (d.status || '').toLowerCase() === 'encamino' &&
      !Boolean(d.treinta_minutos_enviado)
    );

    if (!candidates.length) return;

    notifyKickoffRef.current = true; // fire once per mount

    const token = localStorage.getItem('token');
    
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/driver_on_way/notify`,
        { deliveryIds: candidates.map(c => c.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(({ data }) => {
        // Optimistic local flip so we don't re-send on re-mount
        setDeliveries(prev =>
          prev.map(d =>
            candidates.find(c => c.id === d.id)
              ? { ...d, treinta_minutos_enviado: true }
              : d
          )
        );
        console.log('driver_on_way notify result:', data);
      })
      .catch(err => {
        console.error('driver_on_way notify failed:', err);
        // allow retry if needed
        notifyKickoffRef.current = false;
      });
  }, [deliveries, setDeliveries]);
}